/**
 * <ta-sessions-table> — primary "what happened in each session?" view.
 *
 * One row per training session, ordered DESC by started_at by default.
 * Sortable by any column (click the header). Click a row → emits
 * `session-row-click` so the parent can switch the session picker into
 * Single mode for that session (the per-pitch detail charts then narrow
 * to that session).
 *
 * Two flavors via the `kind` attribute:
 *   - 'rapsodo' (default) — columns: Date | Type | Pitches | Avg | Peak | Strike% | Primary
 *   - 'pocket-radar'      — columns: Date | Title | Throws | Avg | Peak | Context
 *
 * Set rows via .rows = [...] (already aggregated by db/aggregation.js).
 *
 * Keep this DOM-only — no Chart.js, no async work. The whole point of
 * the table is "fast scan, no surprises."
 */

const RAPSODO_COLUMNS = [
  { id: 'started_at',         label: 'Date',     sortable: true,  align: 'left'   },
  { id: 'title',              label: 'Type',     sortable: true,  align: 'left'   },
  { id: 'count',              label: 'Pitches',  sortable: true,  align: 'right'  },
  { id: 'avg_velo',           label: 'Avg velo', sortable: true,  align: 'right'  },
  { id: 'peak_velo',          label: 'Peak',     sortable: true,  align: 'right'  },
  { id: 'strike_pct',         label: 'Strike %', sortable: true,  align: 'right'  },
  { id: 'primary_pitch_type', label: 'Primary',  sortable: true,  align: 'left'   },
];

const POCKET_RADAR_COLUMNS = [
  { id: 'started_at',      label: 'Date',     sortable: true,  align: 'left'   },
  { id: 'title',           label: 'Title',    sortable: true,  align: 'left'   },
  { id: 'count',           label: 'Throws',   sortable: true,  align: 'right'  },
  { id: 'avg_velo',        label: 'Avg velo', sortable: true,  align: 'right'  },
  { id: 'peak_velo',       label: 'Peak',     sortable: true,  align: 'right'  },
  { id: 'primary_context', label: 'Context',  sortable: true,  align: 'left'   },
];

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

class TaSessionsTable extends HTMLElement {
  constructor() {
    super();
    this._rows = [];
    this._sortColumn = 'started_at';
    this._sortDirection = 'desc';
    this._activeId = null;
  }

  set rows(value) {
    this._rows = Array.isArray(value) ? value : [];
    this.render();
  }
  get rows() { return this._rows; }

  // Highlights a single row to show "this is the session being drilled into".
  set activeSessionId(id) {
    this._activeId = id ?? null;
    this.render();
  }

  get _kind() {
    return this.getAttribute('kind') === 'pocket-radar' ? 'pocket-radar' : 'rapsodo';
  }

  get _columns() {
    return this._kind === 'pocket-radar' ? POCKET_RADAR_COLUMNS : RAPSODO_COLUMNS;
  }

  connectedCallback() {
    this.render();
    this.addEventListener('click', this._onClick = (e) => {
      const headerCell = e.target.closest('[data-sort-col]');
      if (headerCell) {
        const col = headerCell.dataset.sortCol;
        if (col === this._sortColumn) {
          this._sortDirection = this._sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          this._sortColumn = col;
          // Numeric defaults to DESC ("show me biggest first"); strings ASC.
          const colDef = this._columns.find((c) => c.id === col);
          this._sortDirection = colDef?.align === 'right' || col === 'started_at' ? 'desc' : 'asc';
        }
        this.render();
        return;
      }
      // Delete-row button: stop the click from also triggering row-click.
      const deleteBtn = e.target.closest('[data-action="delete-session"]');
      if (deleteBtn) {
        e.stopPropagation();
        const id = deleteBtn.dataset.sessionId;
        this.dispatchEvent(new CustomEvent('session-delete-request', {
          bubbles: true, composed: true, detail: { sessionId: id },
        }));
        return;
      }
      const row = e.target.closest('[data-session-id]');
      if (row) {
        const id = row.dataset.sessionId;
        this.dispatchEvent(new CustomEvent('session-row-click', {
          bubbles: true, composed: true, detail: { sessionId: id },
        }));
      }
    });
  }

  render() {
    if (!this.isConnected) return;

    if (this._rows.length === 0) {
      this.innerHTML = `
        <div class="empty-state empty-state--small">No sessions yet.</div>
      `;
      return;
    }

    const sorted = sortRows(this._rows, this._sortColumn, this._sortDirection);
    const cols = this._columns;

    this.innerHTML = `
      <div class="ta-sessions-table__wrap">
        <table class="ta-sessions-table">
          <thead>
            <tr>
              ${cols.map((c) => {
                const active = c.id === this._sortColumn;
                const arrow = active
                  ? (this._sortDirection === 'asc' ? '↑' : '↓')
                  : '';
                const cls = `ta-sessions-table__th ta-sessions-table__th--${c.align}${active ? ' is-active' : ''}`;
                return `
                  <th class="${cls}" data-sort-col="${c.id}" role="button" tabindex="0">
                    <span>${c.label}</span>
                    <span class="ta-sessions-table__arrow">${arrow}</span>
                  </th>
                `;
              }).join('')}
              <th class="ta-sessions-table__th ta-sessions-table__th--action" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            ${sorted.map((row) => this._renderRow(row, cols)).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  _renderRow(row, cols) {
    const isActive = row.id === this._activeId;
    return `
      <tr class="ta-sessions-table__row${isActive ? ' is-active' : ''}" data-session-id="${row.id}">
        ${cols.map((c) => {
          const cls = `ta-sessions-table__td ta-sessions-table__td--${c.align}`;
          return `<td class="${cls}">${this._renderCell(row, c)}</td>`;
        }).join('')}
        <td class="ta-sessions-table__td ta-sessions-table__td--action">
          <button type="button"
            class="ta-sessions-table__delete"
            data-action="delete-session"
            data-session-id="${row.id}"
            aria-label="Delete this session"
            title="Delete this session and all its data">×</button>
        </td>
      </tr>
    `;
  }

  _renderCell(row, col) {
    const v = row[col.id];
    if (v == null || v === '') return '<span class="ta-sessions-table__null">—</span>';
    switch (col.id) {
      case 'started_at': return formatDate(v);
      case 'title':      return escapeHtml(String(v));
      case 'count':      return String(v);
      case 'avg_velo':
      case 'peak_velo':  return `${v.toFixed(1)} mph`;
      case 'strike_pct': return `${v.toFixed(0)}%`;
      case 'primary_pitch_type': return pitchBadge(String(v));
      case 'primary_context':    return contextLabel(String(v));
      default:           return escapeHtml(String(v));
    }
  }
}

function sortRows(rows, column, direction) {
  const sign = direction === 'asc' ? 1 : -1;
  const out = [...rows];
  out.sort((a, b) => {
    const av = a[column];
    const bv = b[column];
    const aNull = av == null;
    const bNull = bv == null;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
    return String(av).localeCompare(String(bv)) * sign;
  });
  return out;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return DATE_FMT.format(d);
}

function pitchBadge(type) {
  if (!type) return '—';
  const cls = `pitch-badge pitch-badge--${type.toLowerCase()}`;
  return `<span class="${cls}">${escapeHtml(type)}</span>`;
}

const CONTEXT_LABELS = {
  warmup: 'Warmup', drill: 'Drill', long_toss: 'Long toss',
  mound: 'Mound', bullpen: 'Bullpen', other: 'Other',
};
function contextLabel(c) { return CONTEXT_LABELS[c] ?? c; }

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

customElements.define('ta-sessions-table', TaSessionsTable);
